import React, { useState, useRef, useEffect } from 'react';
import { Bell, Plus, Clock, AlertCircle, CheckCircle2, ArrowRight, Check, ListChecks } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { getDashboardData } from '../lib/boards';
import { supabase } from '../lib/supabase';
import { getUserNotificationTasks, getUserNotificationTasksCount, TaskDropdownItem } from '../lib/taskNotifications';
import { updateCard } from '../lib/kanban';

interface TasksDropdownProps {
  className?: string;
}

export const TasksDropdown: React.FC<TasksDropdownProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const [isOpen, setIsOpen] = useState(false);
  const [tasks, setTasks] = useState<TaskDropdownItem[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [userType, setUserType] = useState<'admin' | 'resource' | null>(null);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [resourceName, setResourceName] = useState<string | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [completingTasks, setCompletingTasks] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const realtimeUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // Load tasks from notification preferences
  const loadTasks = async () => {
    if (!user?.id || !selectedBusiness?.id) return;

    setIsLoadingTasks(true);
    try {
      console.log('🔍 TasksDropdown: Loading notification tasks...');
      
      // Get tasks from user's notification preferences
      const notificationTasks = await getUserNotificationTasks(user.id, selectedBusiness.id);
      setTasks(notificationTasks);
      
      // Get task count for badge
      const count = await getUserNotificationTasksCount(user.id, selectedBusiness.id);
      setTaskCount(count);
      
      console.log('✅ TasksDropdown: Tasks loaded:', {
        taskCount: notificationTasks.length,
        pendingCount: count
      });
      
    } catch (error) {
      console.error('❌ TasksDropdown: Error loading tasks:', error);
      setTasks([]);
      setTaskCount(0);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  // Load user type information when component mounts
  useEffect(() => {
    const loadUserInfo = async () => {
      if (!user || !selectedBusiness || !supabase) {
        setIsLoadingUser(false);
        return;
      }

      try {
        console.log('🔍 TasksDropdown: Loading user type info...');
        
        // Get user type and access info using the same logic as Tasks page
        const dashboardData = await getDashboardData(selectedBusiness.id, user.id);
        
        // Set user type and info
        setUserType(dashboardData.userType);
        setResourceId(dashboardData.resourceId || null);
        setResourceName(dashboardData.resourceName || null);
        
        console.log('✅ TasksDropdown: User type loaded:', {
          userType: dashboardData.userType,
          resourceId: dashboardData.resourceId,
          resourceName: dashboardData.resourceName
        });
        
      } catch (error) {
        console.error('❌ TasksDropdown: Error loading user info:', error);
        setUserType('admin'); // Default fallback
        setResourceId(null);
        setResourceName(null);
      } finally {
        setIsLoadingUser(false);
      }
    };

    loadUserInfo();
  }, [user, selectedBusiness]);

  // Load tasks when user info is ready
  useEffect(() => {
    if (user?.id && selectedBusiness?.id && userType) {
      loadTasks();
    }
  }, [user?.id, selectedBusiness?.id, userType]);

  // Reload tasks when dropdown opens (to ensure fresh data)
  useEffect(() => {
    if (isOpen && user?.id && selectedBusiness?.id) {
      loadTasks();
    }
  }, [isOpen, user?.id, selectedBusiness?.id]);

  // Set up real-time subscription for task updates
  useEffect(() => {
    if (!user?.id || !selectedBusiness?.id || !supabase) return;

    console.log('🔄 TasksDropdown: Setting up real-time subscription...');

    // Subscribe to changes in task_cards table
    const subscription = supabase
      .channel('task_cards_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all changes (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'task_cards',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        async (payload) => {
          console.log('🔔 TasksDropdown: Task change detected:', payload);
          
          // Debounce rapid real-time updates to prevent excessive API calls
          if (realtimeUpdateTimeoutRef.current) {
            clearTimeout(realtimeUpdateTimeoutRef.current);
          }
          
          realtimeUpdateTimeoutRef.current = setTimeout(async () => {
            // Reload tasks and count when any task changes
            // This ensures the dropdown stays in sync with real-time updates
            if (user?.id && selectedBusiness?.id) {
              try {
                const count = await getUserNotificationTasksCount(user.id, selectedBusiness.id);
                setTaskCount(count);
                
                // Also reload the task list if dropdown is open
                if (isOpen) {
                  const notificationTasks = await getUserNotificationTasks(user.id, selectedBusiness.id);
                  setTasks(notificationTasks);
                }
              } catch (error) {
                console.error('❌ TasksDropdown: Error updating tasks:', error);
              }
            }
          }, 500); // 500ms debounce
        }
      )
      .subscribe();

    return () => {
      console.log('🔄 TasksDropdown: Cleaning up real-time subscription...');
      subscription.unsubscribe();
      
      // Clean up any pending timeout
      if (realtimeUpdateTimeoutRef.current) {
        clearTimeout(realtimeUpdateTimeoutRef.current);
      }
    };
  }, [user?.id, selectedBusiness?.id, isOpen]);

  // Set up real-time subscription for notification preference changes
  useEffect(() => {
    if (!user?.id || !selectedBusiness?.id || !supabase) return;

    console.log('🔄 TasksDropdown: Setting up notification preferences subscription...');

    // Subscribe to changes in user_column_notifications table
    const notificationSubscription = supabase
      .channel('user_column_notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all changes (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'user_column_notifications',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('🔔 TasksDropdown: Notification preference change detected:', payload);
          
          // Debounce rapid real-time updates to prevent excessive API calls
          if (realtimeUpdateTimeoutRef.current) {
            clearTimeout(realtimeUpdateTimeoutRef.current);
          }
          
          realtimeUpdateTimeoutRef.current = setTimeout(async () => {
            // Reload tasks and count when notification preferences change
            if (user?.id && selectedBusiness?.id) {
              try {
                const count = await getUserNotificationTasksCount(user.id, selectedBusiness.id);
                setTaskCount(count);
                
                // Also reload the task list if dropdown is open
                if (isOpen) {
                  const notificationTasks = await getUserNotificationTasks(user.id, selectedBusiness.id);
                  setTasks(notificationTasks);
                }
              } catch (error) {
                console.error('❌ TasksDropdown: Error updating notification tasks:', error);
              }
            }
          }, 500); // 500ms debounce
        }
      )
      .subscribe();

    return () => {
      console.log('🔄 TasksDropdown: Cleaning up notification preferences subscription...');
      notificationSubscription.unsubscribe();
      
      // Clean up any pending timeout
      if (realtimeUpdateTimeoutRef.current) {
        clearTimeout(realtimeUpdateTimeoutRef.current);
      }
    };
  }, [user?.id, selectedBusiness?.id, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getStatusIcon = (status: TaskDropdownItem['status']) => {
    switch (status) {
      case 'scheduled':
        return <Clock size={14} className="text-blue-500" />;
      case 'in-progress':
        return <AlertCircle size={14} className="text-yellow-500" />;
      case 'completed':
        return <CheckCircle2 size={14} className="text-green-500" />;
      default:
        return <Clock size={14} className="text-gray-500" />;
    }
  };

  const getUrgencyColor = (urgency: TaskDropdownItem['urgency']) => {
    switch (urgency) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'alert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleTaskClick = (taskId: string) => {
    // Close dropdown and navigate to tasks page with specific task focused
    setIsOpen(false);
    navigate('/tasks');
    // TODO: Add functionality to focus specific task
  };

  const handleCompleteTask = async (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent task click event
    
    if (completingTasks.has(taskId)) return; // Prevent double-clicking
    
    setCompletingTasks(prev => new Set(prev).add(taskId));
    
    try {
      // Mark task as completed
      await updateCard(taskId, {
        status: 'completed'
      });
      
      // Remove the task from the list immediately
      setTasks(prev => prev.filter(task => task.id !== taskId));
      
      // Update task count
      setTaskCount(prev => Math.max(0, prev - 1));

      // Clean up completing state
      setCompletingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
      
    } catch (error) {
      console.error('Error completing task:', error);
      // Remove from completing set on error
      setCompletingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleViewAllTasks = () => {
    setIsOpen(false);
    navigate('/tasks');
  };

  const hasUnreadTasks = taskCount > 0;

  // Don't render if user info isn't loaded yet
  if (isLoadingUser || !userType) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          disabled
          className="relative flex items-center space-x-2 text-white/50 p-2 opacity-50 cursor-not-allowed"
          title="Loading tasks..."
        >
          <Bell className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Tasks Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center space-x-2 text-white/90 hover:text-white transition-all duration-300 ease-out p-2 rounded-xl hover:bg-white/15 transform hover:scale-105 active:scale-95 touch-manipulation"
        title={`In-Progress Tasks (${userType === 'admin' ? 'Admin' : 'Resource'})`}
      >
        <Bell className="h-5 w-5" />
        
        {/* Notification Badge */}
        {hasUnreadTasks && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium shadow-lg">
            {taskCount > 9 ? '9+' : taskCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-3 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 z-50 max-h-96 overflow-hidden transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.05)'
        }}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-purple-100 rounded-lg">
                  <ListChecks size={16} className="text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900">In-Progress Tasks</h3>
              </div>
              <span className="text-xs text-purple-600 bg-purple-100 px-2.5 py-1 rounded-full font-medium">
                {isLoadingTasks ? 'Loading...' : `${taskCount} in progress`}
              </span>
            </div>
          </div>

          {/* Tasks List */}
          <div className="max-h-64 overflow-y-auto">
            {/* No Tasks Message */}
            {!isLoadingTasks && tasks.length === 0 && (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <ListChecks size={20} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 mb-1">No in-progress tasks available</p>
                <p className="text-xs text-gray-400">Enable notifications on task lists to see in-progress items</p>
              </div>
            )}

            {/* Loading State */}
            {isLoadingTasks && (
              <div className="p-8 text-center">
                <div className="w-8 h-8 mx-auto mb-2 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-500">Loading tasks...</p>
              </div>
            )}

            {/* Tasks List */}
            {!isLoadingTasks && tasks.length > 0 && (
              <div className="py-2">
                {tasks.slice(0, 5).map((task, index) => (
                  <div
                    key={task.id}
                    className={`px-6 py-4 border-b border-gray-100/50 last:border-b-0 transition-all duration-300 ease-out hover:bg-gray-50/80 group ${
                      completingTasks.has(task.id) 
                        ? 'bg-green-50/80 opacity-75' 
                        : ''
                    }`}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Status Icon */}
                      <div className="mt-0.5 transition-transform duration-200 group-hover:scale-110">
                        {getStatusIcon(task.status)}
                      </div>

                      {/* Task Content - Clickable */}
                      <div 
                        className="flex-1 min-w-0 cursor-pointer transition-all duration-200"
                        onClick={() => handleTaskClick(task.id)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-purple-700 transition-colors duration-200">
                            {task.title}
                          </p>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all duration-200 ${getUrgencyColor(task.urgency)}`}>
                            {task.urgency}
                          </span>
                        </div>
                        
                        {task.description && (
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2 group-hover:text-gray-700 transition-colors duration-200">
                            {task.description}
                          </p>
                        )}
                        
                        <div className="flex items-center text-xs text-gray-500 space-x-2 group-hover:text-gray-600 transition-colors duration-200">
                          <span className="font-medium">{task.board_title}</span>
                          <span>•</span>
                          <span>{task.column_title}</span>
                          {task.scheduled_date && (
                            <>
                              <span>•</span>
                              <span className="flex items-center space-x-1">
                                <Clock size={10} />
                                <span>Today</span>
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Complete Button */}
                      {task.status !== 'completed' && (
                        <button
                          onClick={(e) => handleCompleteTask(task.id, e)}
                          disabled={completingTasks.has(task.id)}
                          className="flex-shrink-0 p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 transform hover:scale-110 active:scale-95 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed group-hover:opacity-100"
                          title="Mark as complete"
                        >
                          <Check size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {tasks.length > 5 && (
                  <div className="px-4 py-2 text-center border-t border-gray-100">
                    <button
                      onClick={handleViewAllTasks}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      +{tasks.length - 5} more tasks
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50/80 to-white/80 border-t border-gray-200/50 flex items-center justify-center backdrop-blur-sm">
            <button
              onClick={handleViewAllTasks}
              className="flex items-center space-x-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-all duration-200 hover:bg-purple-50 px-3 py-2 rounded-xl transform hover:scale-105 active:scale-95 touch-manipulation"
            >
              <span>View all tasks</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 